import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useVisibilityRefresh,
  useCombinedRefetch,
  DEFAULT_POLLING_INTERVAL,
} from "../../hooks/useRealtimeQuery";
import {
  selectTherapistProfile,
  selectClinicProfile,
  selectClinicId,
  selectTherapistId,
  selectCanCreateGreenInvoice,
  selectIsDigitalInvoiceActive,
  selectIsSecretary,
  selectHasPermission,
} from "../../store/slices/authSlice";
import { NoAccessState } from "../../components/shared/access";
import {
  useGetAppointmentsQuery,
  useGetMonthlyIncomeStatsQuery,
  useSyncStaleScheduledAppointmentsMutation,
  useBackfillCompletionTasksMutation,
} from "../../store/services/calendarApi";
import { useToast } from "../../contexts/ToastContext";
import { useDayKey } from "../../hooks/useDayKey";
import { useGetPatientsQuery, PATIENTS_PICKER_PAGE_SIZE } from "../../store/services/patientsApi";
import {
  useGetTasksQuery,
  useGetRemindersQuery,
} from "../../store/services/tasksApi";
import {
  useGetNotificationsQuery,
  useMarkAsReadMutation,
} from "../../store/services/notificationsApi";
import AppointmentModal from "../../components/calendar/AppointmentEntryModal";
import BookingApprovalModal from "../../components/calendar/BookingApprovalModal";
import CollapsibleSections from "../../components/dashboard/CollapsibleSections";
import CatchUpMode from "../../components/dashboard/catchup/CatchUpMode";
import useCatchUpItems from "../../components/dashboard/catchup/useCatchUpItems";
import QuickActionsFAB from "../../components/dashboard/QuickActionsFAB";
import FeatureTipsCard from "../../components/dashboard/FeatureTipsCard";
// import OnboardingChecklist from "../../components/dashboard/OnboardingChecklist";
import InvoiceQuickModal from "../../components/morning/InvoiceQuickModal";
import DigitalInvoiceQuickModal from "../../components/digitalInvoice/DigitalInvoiceQuickModal";
import { StatItem, StatItemGrid, HelpButton, PageHeader } from "../../components/shared";
import { getDefaultHomePageSettings, HOMEPAGE_SECTIONS } from "./SettingsPage";
import {
  Calendar,
  Users,
  CheckSquare,
  CurrencyCircleDollar,
  Plus,
  CalendarPlus,
  NotePencil,
  Bell,
  Check,
  Clock,
  Hourglass,
  CircleDashed,
  HandWaving,
  House,
  CalendarCheck,
  Note,
  ArrowsClockwise,
  Lightning,
} from "@phosphor-icons/react";
// Re-enable when the commented-out manual-sync buttons below are restored:
// import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import "./HomePage.css";

// Status configuration for tasks - same as TasksRemindersPage
const TASK_STATUSES = [
  { value: "todo", label: "פתוח", icon: CircleDashed, color: "#4dd9b4" },
  { value: "in_progress", label: "בתהליך", icon: Hourglass, color: "#3b82f6" },
  { value: "waiting", label: "ממתין", icon: Clock, color: "#f59e0b" },
  { value: "done", label: "הסתיים", icon: Check, color: "#22c55e" },
];

// Sort order for tasks (בתהליך first, then ממתין, then פתוח, then הסתיים)
const STATUS_SORT_ORDER = {
  in_progress: 0,
  waiting: 1,
  todo: 2,
  done: 3,
};

const AUTO_REFRESH_KEY = "clinikit_last_refresh";
const AUTO_REFRESH_INTERVAL_MS = 48 * 60 * 60 * 1000; // 48 hours

function HomePage() {
  const navigate = useNavigate();
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);

  useEffect(() => {
    const lastRefresh = localStorage.getItem(AUTO_REFRESH_KEY);
    const now = Date.now();

    if (!lastRefresh || now - Number(lastRefresh) >= AUTO_REFRESH_INTERVAL_MS) {
      localStorage.setItem(AUTO_REFRESH_KEY, String(now));
      if (lastRefresh) {
        window.location.reload(true);
      }
    }
  }, []);

  const isUsingGreenInvoice = useSelector(selectCanCreateGreenInvoice);
  const isDigitalInvoiceActive = useSelector(selectIsDigitalInvoiceActive);

  // Multi-clinic v2 — a secretary without calendar.viewClinic has nothing to
  // show on the home page (no own calendar/patients). Non-secretaries and
  // legacy therapist docs are unaffected (isSecretary is false for them).
  const isSecretary = useSelector(selectIsSecretary);
  const canViewClinicCalendar = useSelector(selectHasPermission("calendar.viewClinic"));
  const secretaryBlocked = isSecretary && canViewClinicCalendar !== true;
  // A secretary WITH clinic-calendar access has no own-scoped data either —
  // their working page is the clinic calendar, so land there.
  const secretaryToClinicCalendar = isSecretary && canViewClinicCalendar === true;
  useEffect(() => {
    if (secretaryToClinicCalendar) navigate("/dashboard/clinic-calendar", { replace: true });
  }, [secretaryToClinicCalendar, navigate]);

  // Catch-up deck — opened via the small trigger near the greeting, shown as a
  // popup. Shares the accordion's queries so both stay in sync in real time.
  const [showCatchUp, setShowCatchUp] = useState(false);
  const { items: catchUpItems, total: catchUpTotal } = useCatchUpItems();

  const { showSuccess, showError } = useToast();
  const [syncStaleAppointments, { isLoading: isSyncing }] =
    useSyncStaleScheduledAppointmentsMutation();
  const [backfillFutureTasks, { isLoading: isBackfilling }] =
    useBackfillCompletionTasksMutation();

  const handleManualSyncClick = async () => {
    if (!clinicId || !therapistId || isSyncing) return;
    try {
      const result = await syncStaleAppointments({
        clinicId,
        therapistId,
      }).unwrap();
      const updated = result?.updated ?? 0;
      if (updated > 0) {
        showSuccess(
          `סונכרנו ${updated} פגישות. הן יופיעו עכשיו ב"ממתינות לטיפול"`,
        );
      } else {
        showSuccess("הכל מסונכרן. אין פגישות שצריך להחזיר.");
      }
    } catch (error) {
      console.error("[CK-3050] Manual sync failed:", {
        errorCode: "CK-3050",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("הסנכרון נכשל. נסי שוב מאוחר יותר.");
    }
  };

  const handleBackfillFutureTasksClick = async () => {
    if (!clinicId || !therapistId || isBackfilling) return;
    try {
      const result = await backfillFutureTasks({
        clinicId,
        therapistId,
      }).unwrap();
      const scheduled = result?.scheduled ?? 0;
      const alreadyHadTask = result?.alreadyHadTask ?? 0;
      const failed = result?.failed ?? 0;

      if (scheduled > 0) {
        showSuccess(
          `סונכרנו ${scheduled} פגישות עתידיות. הן יעברו אוטומטית ל"ממתינות לטיפול" בסיום הפגישה.`,
        );
      } else if (alreadyHadTask > 0) {
        showSuccess(
          `הכל כבר מסונכרן. ${alreadyHadTask} פגישות עתידיות כבר מוגדרות נכון.`,
        );
      } else {
        showSuccess("אין פגישות עתידיות שדורשות סנכרון.");
      }

      if (failed > 0) {
        showError(
          `${failed} פגישות נכשלו בסנכרון. צור קשר עם התמיכה אם זה חוזר על עצמו.`,
        );
      }
    } catch (error) {
      console.error("[CK-3051] Backfill future tasks failed:", {
        errorCode: "CK-3051",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("הסנכרון נכשל. נסי שוב מאוחר יותר.");
    }
  };

  // Modal states
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  // Self-booked request awaiting approval (opens the approval modal)
  const [approvalAppointment, setApprovalAppointment] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Hidden reminders state (persisted in localStorage)
  const [hiddenReminderIds, setHiddenReminderIds] = useState(() => {
    try {
      const stored = localStorage.getItem(
        `clinikit_hidden_reminders_${therapistId}`,
      );
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [showHiddenReminders, setShowHiddenReminders] = useState(false);

  // Mark notification as read mutation
  const [markAsRead] = useMarkAsReadMutation();

  // Get home page settings from profile (with defaults)
  // Always use default stats (6 cards) - no user customization for now
  const homePageSettings = useMemo(() => {
    const defaults = getDefaultHomePageSettings();
    if (!therapistProfile?.homePageSettings) return defaults;

    // Get saved sections or use defaults
    let sections =
      therapistProfile.homePageSettings.sections || defaults.sections;

    // Merge with defaults to include any new sections that were added after user saved their settings
    const existingSectionIds = sections.map((s) => s.id);
    const newSections = defaults.sections.filter(
      (s) => !existingSectionIds.includes(s.id),
    );
    if (newSections.length > 0) {
      // Add new sections before quickActions (or at the end if quickActions not found)
      const quickActionsIndex = sections.findIndex(
        (s) => s.id === "quickActions",
      );
      if (quickActionsIndex !== -1) {
        sections = [
          ...sections.slice(0, quickActionsIndex),
          ...newSections,
          ...sections.slice(quickActionsIndex),
        ];
      } else {
        sections = [...sections, ...newSections];
      }
    }

    return {
      // Always use default stats (all 6) - ignore saved user preferences for stats
      selectedStats: defaults.selectedStats,
      sections,
    };
  }, [therapistProfile?.homePageSettings]);

  // ==========================================
  // DATE RANGES
  // ==========================================

  // Rolls over at midnight so an open dashboard doesn't freeze on yesterday
  const dayKey = useDayKey();

  // Today's date range
  const todayRange = useMemo(() => {
    const today = new Date();
    const start = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0,
    );
    const end = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
    );
    return { start, end };
  }, [dayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Upcoming 7 days (excluding today)
  const upcomingRange = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const end = new Date(tomorrow);
    end.setDate(end.getDate() + 7);
    end.setHours(23, 59, 59, 999);
    return { start: tomorrow, end };
  }, [dayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rest of the month (from today until end of month)
  const restOfMonthRange = useMemo(() => {
    const today = new Date();
    const start = new Date(today);
    start.setHours(0, 0, 0, 0);
    const end = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0,
      23,
      59,
      59,
      999,
    );
    return { start, end };
  }, [dayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================
  // DATA FETCHING
  // ==========================================

  // Fetch today's appointments with real-time updates
  const { data: todayAppointments = [], refetch: refetchToday } =
    useGetAppointmentsQuery(
      {
        clinicId,
        therapistId,
        startDate: todayRange.start.toISOString(),
        endDate: todayRange.end.toISOString(),
      },
      {
        skip: !clinicId || !therapistId,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      },
    );

  // Fetch upcoming appointments with real-time updates
  const { data: upcomingAppointments = [], refetch: refetchUpcoming } =
    useGetAppointmentsQuery(
      {
        clinicId,
        therapistId,
        startDate: upcomingRange.start.toISOString(),
        endDate: upcomingRange.end.toISOString(),
      },
      {
        skip: !clinicId || !therapistId,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      },
    );

  // Fetch patients (for count)
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, therapistId, status: "active", pageSize: 200 },
    { skip: !clinicId || !therapistId },
  );

  // Fetch personal tasks with real-time updates
  const { data: personalTasks = [], refetch: refetchPersonalTasks } =
    useGetTasksQuery(
      { clinicId, scope: "personal", therapistId },
      {
        skip: !clinicId || !therapistId,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      },
    );

  // Fetch clinic tasks assigned to me with real-time updates
  const { data: clinicTasks = [], refetch: refetchClinicTasks } =
    useGetTasksQuery(
      { clinicId, scope: "clinic" },
      { skip: !clinicId, pollingInterval: DEFAULT_POLLING_INTERVAL },
    );

  // Fetch notifications with real-time updates
  const { data: notificationsData, refetch: refetchNotifications } =
    useGetNotificationsQuery(
      { clinicId, therapistId, pageSize: 20 },
      {
        skip: !clinicId || !therapistId,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      },
    );

  // Fetch patients for modal dropdown
  const { data: allPatientsData } = useGetPatientsQuery(
    { clinicId, therapistId, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId || !therapistId },
  );

  // Fetch rest of month appointments (for future appointments stat)
  const { data: restOfMonthAppointments = [], refetch: refetchRestOfMonth } =
    useGetAppointmentsQuery(
      {
        clinicId,
        therapistId,
        startDate: restOfMonthRange.start.toISOString(),
        endDate: restOfMonthRange.end.toISOString(),
      },
      {
        skip: !clinicId || !therapistId,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      },
    );

  // Fetch monthly income stats with real-time updates
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const { data: incomeStats, refetch: refetchIncomeStats } =
    useGetMonthlyIncomeStatsQuery(
      { clinicId, therapistId, year: currentYear, month: currentMonth },
      {
        skip: !clinicId || !therapistId,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      },
    );

  // Fetch personal reminders with real-time updates
  const { data: personalReminders = [], refetch: refetchPersonalReminders } =
    useGetRemindersQuery(
      { clinicId, scope: "personal", therapistId },
      {
        skip: !clinicId || !therapistId,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      },
    );

  // Fetch clinic reminders with real-time updates
  const { data: clinicReminders = [], refetch: refetchClinicReminders } =
    useGetRemindersQuery(
      { clinicId, scope: "clinic" },
      { skip: !clinicId, pollingInterval: DEFAULT_POLLING_INTERVAL },
    );

  // Combine all refetch functions for visibility refresh
  const combinedRefetch = useCombinedRefetch([
    refetchToday,
    refetchUpcoming,
    refetchPersonalTasks,
    refetchClinicTasks,
    refetchNotifications,
    refetchRestOfMonth,
    refetchIncomeStats,
    refetchPersonalReminders,
    refetchClinicReminders,
  ]);

  // Auto-refresh all data when tab becomes visible
  useVisibilityRefresh(combinedRefetch);

  // ==========================================
  // COMPUTED VALUES
  // ==========================================

  // Active patients count
  const activePatientsCount = patientsData?.patients?.length || 0;

  // Combine and filter tasks
  const allTasks = useMemo(() => {
    const myClinicTasks = clinicTasks.filter(
      (task) => task.assignedTo === therapistId,
    );
    const combined = [...personalTasks, ...myClinicTasks];
    // Sort by status priority
    return combined.sort((a, b) => {
      const orderA = STATUS_SORT_ORDER[a.status] ?? 99;
      const orderB = STATUS_SORT_ORDER[b.status] ?? 99;
      return orderA - orderB;
    });
  }, [personalTasks, clinicTasks, therapistId]);

  // Open tasks count (not done)
  const openTasksCount = allTasks.filter((t) => t.status !== "done").length;

  // Notifications - only unread ones
  const notifications = (notificationsData?.notifications || []).filter(
    (n) => !n.readBy?.includes(therapistId),
  );

  // Today's appointments count (blocked time is not a meeting; cancelled don't count)
  const todayAppointmentsCount = todayAppointments.filter(
    (apt) => apt.isBlockedTime !== true && apt.status !== "cancelled",
  ).length;

  // Future appointments this month (excluding today)
  const futureAppointmentsCount = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return restOfMonthAppointments.filter(
      (apt) =>
        new Date(apt.startTime) > today &&
        apt.status !== "cancelled" &&
        apt.isBlockedTime !== true,
    ).length;
  }, [restOfMonthAppointments, dayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pending collection amount
  const pendingCollectionAmount = incomeStats?.pendingCollectionAmount || 0;

  // Combine personal and clinic reminders (clinic created by this therapist)
  const allReminders = useMemo(() => {
    const myClinicReminders = clinicReminders.filter(
      (reminder) => reminder.createdBy === therapistId,
    );
    return [...personalReminders, ...myClinicReminders].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  }, [personalReminders, clinicReminders, therapistId]);

  // Filter out hidden reminders for display
  const visibleReminders = useMemo(() => {
    if (showHiddenReminders) {
      return allReminders;
    }
    return allReminders.filter((r) => !hiddenReminderIds.includes(r.id));
  }, [allReminders, hiddenReminderIds, showHiddenReminders]);

  // Count of hidden reminders
  const hiddenRemindersCount = useMemo(() => {
    return allReminders.filter((r) => hiddenReminderIds.includes(r.id)).length;
  }, [allReminders, hiddenReminderIds]);

  // Get lead status config helper
  // Format income value
  const formatIncome = (amount) => {
    if (!amount && amount !== 0) return "₪0";
    return `₪${amount.toLocaleString("he-IL")}`;
  };

  // All available stats configuration
  const STATS_CONFIG = {
    todayAppointments: {
      label: "פגישות היום",
      value: todayAppointmentsCount.toString(),
      icon: Calendar,
      color: "primary",
    },
    activePatients: {
      label: "מטופלים פעילים",
      value: activePatientsCount.toString(),
      icon: Users,
      color: "purple",
    },
    openTasks: {
      label: "משימות פתוחות",
      value: openTasksCount.toString(),
      icon: CheckSquare,
      color: "yellow",
    },
    monthlyIncome: {
      label: "הכנסות החודש",
      value: formatIncome(incomeStats?.actualIncome),
      icon: CurrencyCircleDollar,
      color: "green",
    },
    futureAppointments: {
      label: "פגישות עתידיות החודש",
      value: futureAppointmentsCount.toString(),
      icon: CalendarCheck,
      color: "primary",
    },
    pendingCollection: {
      label: "ממתין לגבייה",
      value: formatIncome(pendingCollectionAmount),
      icon: Hourglass,
      color: "yellow",
    },
  };

  // Build stats array dynamically based on settings
  const stats = useMemo(() => {
    return homePageSettings.selectedStats
      .map((statId, index) => {
        const config = STATS_CONFIG[statId];
        if (!config) return null;
        return {
          id: index + 1,
          ...config,
        };
      })
      .filter(Boolean);
  }, [homePageSettings.selectedStats, STATS_CONFIG]);

  // Get visible sections based on settings
  const visibleSections = useMemo(() => {
    return homePageSettings.sections.filter((s) => s.visible);
  }, [homePageSettings.sections]);

  // Check if a section is visible
  const isSectionVisible = (sectionId) => {
    return visibleSections.some((s) => s.id === sectionId);
  };

  // ==========================================
  // ONBOARDING CONDITIONS
  // ==========================================

  // Check if user has patients
  const hasPatients = activePatientsCount > 0;

  // Check if user has any appointments (today or future)
  const hasAppointments =
    todayAppointments.length > 0 ||
    upcomingAppointments.length > 0 ||
    restOfMonthAppointments.length > 0;

  // Check if hourly rate is set (in clinic settings)
  const hasHourlyRate =
    clinicProfile?.defaultHourlyRate !== null &&
    clinicProfile?.defaultHourlyRate !== undefined &&
    clinicProfile?.defaultHourlyRate > 0;

  const hasInvoiceSystem = isUsingGreenInvoice;

  // Check if user has read at least one notification
  const hasReadNotification = useMemo(() => {
    const allNotifications = notificationsData?.notifications || [];
    return allNotifications.some((n) => n.readBy?.includes(therapistId));
  }, [notificationsData?.notifications, therapistId]);

  // ==========================================
  // HELPER FUNCTIONS
  // ==========================================

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "בוקר טוב";
    if (hour < 17) return "צהריים טובים";
    if (hour < 21) return "ערב טוב";
    return "לילה טוב";
  };

  // Get status color for appointment (same as DaySummary)
  const getStatusColor = (apt) => {
    if (apt.isBlockedTime === true) return "blocked";
    if (apt.status === "pending_approval") return "amber";
    if (apt.status === "cancelled") return "red";
    // Any confirmation declined - show red
    if (
      apt.whatsappInitialDeclined === true ||
      apt.whatsappReminderDeclined === true
    )
      return "red";
    // Both confirmations received - turquoise (double confirmed)
    if (
      apt.whatsappInitialConfirmed === true &&
      apt.whatsappReminderConfirmed === true
    )
      return "turquoise";
    // At least one confirmed (and none declined) - turquoise
    if (
      apt.whatsappInitialConfirmed === true ||
      apt.whatsappReminderConfirmed === true
    )
      return "turquoise";
    // All are null - waiting for confirmation - yellow
    return "yellow";
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("he-IL", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  // Format notification timestamp (the event time)
  const formatNotificationTimestamp = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format relative time for when notification was created
  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `לפני ${diffMins} דק׳`;
    if (diffHours < 24) return `לפני ${diffHours} שע׳`;
    if (diffDays === 1) return "אתמול";
    if (diffDays < 7) return `לפני ${diffDays} ימים`;
    return date.toLocaleDateString("he-IL", { day: "numeric", month: "short" });
  };

  // Get task status config
  const getTaskStatusConfig = (status) => {
    return TASK_STATUSES.find((s) => s.value === status) || TASK_STATUSES[0];
  };

  // Get urgency class for notifications (same as NotificationsDropdown)
  const getUrgencyClass = (urgency) => {
    if (urgency === 3) return "urgency-high";
    if (urgency === 2) return "urgency-medium";
    return "urgency-low";
  };

  // Sorted today appointments (copy to avoid mutating RTK Query cache)
  const sortedTodayAppointments = useMemo(() => {
    return [...todayAppointments].sort(
      (a, b) => new Date(a.startTime) - new Date(b.startTime),
    );
  }, [todayAppointments]);

  // Group upcoming appointments by day
  const groupedUpcoming = useMemo(() => {
    const groups = {};
    // Copy array to avoid mutating RTK Query cache
    [...upcomingAppointments]
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .forEach((apt) => {
        const dateKey = new Date(apt.startTime).toLocaleDateString("he-IL");
        if (!groups[dateKey]) {
          groups[dateKey] = {
            dateLabel: formatDate(apt.startTime),
            appointments: [],
          };
        }
        groups[dateKey].appointments.push(apt);
      });
    return Object.values(groups).slice(0, 3); // Show max 3 days
  }, [upcomingAppointments]);

  // ==========================================
  // HANDLERS
  // ==========================================

  const handleOpenAppointmentModal = (date = null) => {
    setPrefilledDate(date);
    setSelectedAppointment(null);
    setShowAppointmentModal(true);
  };

  const handleEditAppointment = (appointment) => {
    // Pending self-booking requests open the approval modal, not the editor.
    if (appointment?.status === "pending_approval") {
      setApprovalAppointment(appointment);
      return;
    }
    setSelectedAppointment(appointment);
    setPrefilledDate(null);
    setShowAppointmentModal(true);
  };

  // Self-booked requests across all loaded ranges, awaiting therapist approval.
  const pendingBookings = useMemo(() => {
    const all = [
      ...todayAppointments,
      ...upcomingAppointments,
      ...restOfMonthAppointments,
    ];
    const seen = new Set();
    return all.filter((a) => {
      if (a?.status !== "pending_approval" || seen.has(a.id)) return false;
      seen.add(a.id);
      return true;
    });
  }, [todayAppointments, upcomingAppointments, restOfMonthAppointments]);

  const handleCloseAppointmentModal = () => {
    setPrefilledDate(null);
    setSelectedAppointment(null);
    setShowAppointmentModal(false);
  };

  const handleNotificationClick = async (notification) => {
    // Navigate if link exists
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAsRead = async (e, notification) => {
    e.stopPropagation();
    try {
      await markAsRead({
        clinicId,
        notificationId: notification.id,
        therapistId,
      });
    } catch (error) {
      console.error("[CK-0001] Error marking notification as read:", error);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case "new-appointment":
        handleOpenAppointmentModal();
        break;
      case "new-patient":
        navigate("/dashboard/patients/new");
        break;
      case "treatment-summary":
        navigate("/dashboard/patients");
        break;
      case "add-reminder":
        navigate("/dashboard/tasks");
        break;
      case "new-lead":
        navigate("/dashboard/tasks?tab=leads&action=new");
        break;
      case "create-invoice":
        setShowInvoiceModal(true);
        break;
      default:
        break;
    }
  };

  // Hide a reminder from the preview
  const handleHideReminder = (e, reminderId) => {
    e.stopPropagation();
    const newHiddenIds = [...hiddenReminderIds, reminderId];
    setHiddenReminderIds(newHiddenIds);
    try {
      localStorage.setItem(
        `clinikit_hidden_reminders_${therapistId}`,
        JSON.stringify(newHiddenIds),
      );
    } catch (error) {
      console.error("[CK-HP01] Error saving hidden reminders:", error);
    }
  };

  // Show a hidden reminder again
  const handleShowReminder = (e, reminderId) => {
    e.stopPropagation();
    const newHiddenIds = hiddenReminderIds.filter((id) => id !== reminderId);
    setHiddenReminderIds(newHiddenIds);
    try {
      localStorage.setItem(
        `clinikit_hidden_reminders_${therapistId}`,
        JSON.stringify(newHiddenIds),
      );
    } catch (error) {
      console.error("[CK-HP02] Error saving hidden reminders:", error);
    }
  };

  // Toggle showing hidden reminders
  const toggleShowHiddenReminders = () => {
    setShowHiddenReminders(!showHiddenReminders);
  };

  // Handler for FAB to open invoice modal
  const handleFABInvoice = () => {
    setShowInvoiceModal(true);
  };

  // ==========================================
  // ONBOARDING HANDLERS
  // ==========================================

  const handleOnboardingNewPatient = () => {
    navigate("/dashboard/patients/new");
  };

  const handleOnboardingNewAppointment = () => {
    handleOpenAppointmentModal();
  };

  const handleOnboardingGoToSettings = () => {
    navigate("/dashboard/settings");
  };

  const handleOnboardingGoToMorningSetup = () => {
    navigate("/dashboard/admin/settings");
  };

  // ==========================================
  // RENDER
  // ==========================================

  // All hooks above have run — safe to branch here.
  if (secretaryToClinicCalendar) return null; // redirecting (effect above)
  if (secretaryBlocked) {
    return (
      <div className="home-page">
        <PageHeader
          icon={House}
          title={
            <>
              {getGreeting()}, {therapistProfile?.firstName || "שם"}
              <HandWaving size={28} weight="fill" className="wave-icon" />
            </>
          }
          actions={<HelpButton featureKey="home_dashboard" />}
        />
        <NoAccessState
          size="page"
          message="המנהל/ת טרם פתח/ה עבורך גישה ליומן הקליניקה."
        />
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Welcome Section */}
      <PageHeader
        icon={House}
        title={
          <>
            {getGreeting()}, {therapistProfile?.firstName || "שם"}
            <HandWaving size={28} weight="fill" className="wave-icon" />
          </>
        }
        subtitle="הנה סיכום היום שלך"
        actions={<HelpButton featureKey="home_dashboard" />}
      />
      {/* Catch-up trigger — hidden for now (kept for later re-enable)
      {catchUpTotal > 0 && (
        <button
          type="button"
          className="catchup-trigger"
          onClick={() => setShowCatchUp(true)}
        >
          <Lightning size={18} weight="fill" />
          <span>מצב השלמה</span>
          <span className="catchup-trigger-count">{catchUpTotal}</span>
        </button>
      )} */}

      {/* Unused-feature discovery tip (server-computed, dismissible) */}
      <FeatureTipsCard />

      {/* Pending self-booking requests awaiting therapist approval */}
      {pendingBookings.length > 0 && (
        <div className="pending-approvals">
          <div className="pending-approvals-head">
            <Hourglass size={20} weight="fill" />
            <span>בקשות תור ממתינות לאישור ({pendingBookings.length})</span>
          </div>
          <div className="pending-approvals-list">
            {pendingBookings.map((a) => {
              const d = new Date(a.startTime);
              const when = `${d.toLocaleDateString("he-IL", {
                day: "numeric",
                month: "numeric",
              })} ${d.toLocaleTimeString("he-IL", {
                hour: "2-digit",
                minute: "2-digit",
              })}`;
              return (
                <button
                  key={a.id}
                  type="button"
                  className="pending-approval-item"
                  onClick={() => setApprovalAppointment(a)}
                >
                  <span className="pending-approval-name">
                    {a.bookedByName || a.patientName}
                  </span>
                  <span className="pending-approval-when">{when}</span>
                  <span className="pending-approval-cta">עדכן פגישה זו ←</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* One-time manual sync banner for recovering stuck appointments
      <button
        type="button"
        className="manual-sync-banner"
        onClick={handleManualSyncClick}
        disabled={isSyncing}
      >
        {isSyncing ? (
          <LoaderLoop size="xs" />
        ) : (
          <ArrowsClockwise size={20} weight="bold" />
        )}
        <span className="manual-sync-text">
          {isSyncing
            ? "מסנכרן פגישות..."
            : "לוטם — תלחצי כאן כדי לסנכרן פגישות שלא עברו ל\"ממתינות לטיפול\""}
        </span>
      </button> */}

      {/* One-time backfill: schedule Cloud Tasks for future scheduled
          appointments that have no `completionTaskName` yet, so they will
          auto-transition to "completed" at their endTime. */}
      {/* <button
        type="button"
        className="manual-sync-banner manual-sync-banner--future"
        onClick={handleBackfillFutureTasksClick}
        disabled={isBackfilling}
      >
        {isBackfilling ? (
          <LoaderLoop size="xs" />
        ) : (
          <ArrowsClockwise size={20} weight="bold" />
        )}
        <span className="manual-sync-text">
          {isBackfilling
            ? "מסנכרן פגישות עתידיות..."
            : "סנכרן פגישות עתידיות"}
        </span>
      </button> */}

      {/* Onboarding Checklist - Disabled for now */}
      {/* <OnboardingChecklist
        hasPatients={hasPatients}
        hasAppointments={hasAppointments}
        hasHourlyRate={hasHourlyRate}
        hasMorningConnected={hasInvoiceSystem}
        hasReadNotification={hasReadNotification}
        onNewPatient={handleOnboardingNewPatient}
        onNewAppointment={handleOnboardingNewAppointment}
        onGoToSettings={handleOnboardingGoToSettings}
        onGoToMorningSetup={handleOnboardingGoToMorningSetup}
      /> */}

      {/* Collapsible Sections - Modern Horizontal Tabs with Pending Actions */}
      <CollapsibleSections onEditAppointment={handleEditAppointment} />

      {/* Stats — compact rotating carousel, below catch up */}
      <StatItemGrid columns={6}>
        {stats.map((stat) => {
          // Map color to variant
          const variantMap = {
            primary: "primary",
            green: "success",
            yellow: "warning",
            purple: "purple",
          };
          const variant = variantMap[stat.color] || "primary";

          return (
            <StatItem
              key={`stat-item-${stat.id}`}
              icon={stat.icon}
              value={stat.value}
              label={stat.label}
              variant={variant}
            />
          );
        })}
      </StatItemGrid>

      {/* Desktop FAB for Quick Actions */}
      <QuickActionsFAB
        onNewAppointment={handleOpenAppointmentModal}
        onNewInvoice={handleFABInvoice}
      />

      {/* Appointment Modal */}
      {showAppointmentModal && (
        <AppointmentModal
          appointment={selectedAppointment}
          prefilledDate={prefilledDate}
          patients={allPatientsData?.patients || []}
          onClose={handleCloseAppointmentModal}
        />
      )}

      {/* Booking Approval Modal (self-booked, pending therapist approval) */}
      {approvalAppointment && (
        <BookingApprovalModal
          appointment={approvalAppointment}
          patients={allPatientsData?.patients || []}
          onClose={() => setApprovalAppointment(null)}
          onDecided={() => setApprovalAppointment(null)}
        />
      )}

      {/* Invoice Quick Modal (for FAB and mobile) */}
      {showInvoiceModal &&
        (isDigitalInvoiceActive ? (
          <DigitalInvoiceQuickModal
            isOpen={showInvoiceModal}
            onClose={() => setShowInvoiceModal(false)}
            onSuccess={() => setShowInvoiceModal(false)}
          />
        ) : (
          <InvoiceQuickModal
            isOpen={showInvoiceModal}
            onClose={() => setShowInvoiceModal(false)}
            onSuccess={() => setShowInvoiceModal(false)}
          />
        ))}

      {/* Catch-up deck popup — opened from the trigger near the greeting */}
      {showCatchUp && catchUpTotal > 0 && (
        <CatchUpMode
          items={catchUpItems}
          onClose={() => setShowCatchUp(false)}
        />
      )}
    </div>
  );
}

export default HomePage;
